/* eslint-disable no-use-before-define */
import {Observable} from 'rxjs';
import {Conversation, ConversationContext} from './conversation';
import {ConversationLogItemMessageAdded} from './conversation-log';
import {Analytics, Errors, Metrics} from './observability';

export type BaseEvent = {
  id: string;
  timestamp: number;
};

export enum ClientStatus {
  // The offline status is emitted by clients when the window emits 'offline' events
  // If the status is still offline when the window/ emits a new 'online' event,
  // it will transition to reconnecting.
  // It is up to the individual client implementation to update the state from
  // reconnecting to connected.
  Offline = 'offline',

  // The initial client state is uninitialized. Clients in this state
  // are not necessarily safe to be interacted with via their API.
  // Client implementations should/ handle transitioning from
  // uninitialized -> initialized -> connected as applicable.
  Uninitialized = 'uninitialized',

  // This means the client has been initialized and is safe to interact
  // with via its API.
  // This intermediate status is not necessarily required, but may be
  // useful for client implementations to utilize. An example of a
  // difference between initialized and connected might be once
  // stripethirdparty has been mounted, but the underlying STP client
  // hasn't yet connected.
  Initialized = 'initialized',

  // The connected status means that the client has been initialized and has
  // established a connection with whatever backend provider it uses.
  Connected = 'connected',

  // The connecting status indicates when the client is initially connecting.
  Connecting = 'connecting',

  // The reconnecting status indicates when the client is reconnecting after
  // losing connection.
  Reconnecting = 'reconnecting',

  // The error state is for if something unexpected occurs and the client is in an
  // unrecoverable state.
  Error = 'error',
}

export enum ConversationStatus {
  // Active is the default state for a conversation, whether new or in progress.
  Active = 'active',

  // Inactive conversations are conversations that should no longer be able to
  // be interacted with by an end-user/merchant.
  Inactive = 'inactive',
}

export interface ConversationParticipant {
  id: string;
  role: 'user' | 'agent' | 'bot';
  displayName: string;
}

export enum ConversationMessageStatus {
  Pending = 'pending',
  Sent = 'sent',
  Error = 'error',
}

export type ClientStatusEvent = {
  name: 'client-status';
  status: string;
};

export type ClientExceptionEvent = {
  name: 'client-exception';
  error: string;
  sentryEventId?: string;
};

export type ClientEventType = ClientStatusEvent | ClientExceptionEvent;

export type ClientEvent = ClientEventType & BaseEvent;

export enum ConversationMessageError {
  AttachmentTypeError = 'attachment-type-error',
  AttachmentTooLargeError = 'attachment-too-large-error',
}

export type BaseConversationMessage = {
  id: string;
  participant: ConversationParticipant;
  timestamp: number;
  status?: ConversationMessageStatus;
  nonce?: string;
  errors?: ConversationMessageError[];
  data?: Record<string, unknown>;
  responseId?: string | undefined;
  feedback?: {
    action: 'log_only' | 'thumbs_down_advance' | 'thumbs_up_advance';
    value: 'none' | 'thumbs_down' | 'thumbs_up';
  };
};

export type TextMessage = {
  type: 'text-conversation-message';
  text: string;
  style?: 'bubble' | 'info' | 'none';
  originalText?: string;
};

export type TextConversationMessage = BaseConversationMessage & TextMessage;

export type Attachment = {
  name: string;
  size: number;
  type: string; // MIMEtype
  url?: string;
  file?: File;
};

export type AttachmentMessage = {
  type: 'attachment-conversation-message';
  attachment: Attachment;
};

export type AttachmentConversationMessage = BaseConversationMessage &
  AttachmentMessage;

export type ConversationOption = {
  id: string;
  label: string;
  clickInterceptUrl?: string;
  externalUrl?: string;
};

export type OptionsMessage = {
  id: string;
  label?: string;
  clickInterceptUrl?: string;
  externalUrl?: string;
  options: ConversationOption[];
  selectedOptionId?: string;
  recommendedOptionId?: string;
  purpose?: string;
};

export type ButtonOptionsMessage = OptionsMessage & {
  type: 'button-options-conversation-message';
};

export type ButtonOptionsConversationMessage = BaseConversationMessage &
  ButtonOptionsMessage;

export type SelectOptionsMessage = OptionsMessage & {
  type: 'select-options-conversation-message';
  defaultOptionId?: string;
};

export type SelectOptionsConversationMessage = BaseConversationMessage &
  SelectOptionsMessage;

export type ConversationArticle = {
  id: string;
  title: string;
  url: string;
  collection?:
    | 'support_docs'
    | 'express_support_docs'
    | 'link_support_docs'
    | 'support_topic_pages'
    | 'stripe_docs'
    | 'stripe_api_docs';
};

export type ArticlesMessage = {
  type: 'articles-conversation-message';
  articles: ConversationArticle[];
  articleStyle?: 'bubble' | 'sources_dropdown';
};

export type ArticlesConversationMessage = BaseConversationMessage &
  ArticlesMessage;

export type ConversationButton = {
  title: string;
  url: string;
};

export type ButtonsMessage = {
  type: 'buttons-conversation-message';
  buttons: ConversationButton[];
};

export type AgentPlanItem = {
  uuid: string;
  step: string;
};

export type AgentCallResponse = {
  payment_link?: {
    id: string;
    url: string;
  };
  line_items?: {
    id: string;
    description: string;
    amount_total: number;
    currency: string;
    price: {
      product: string;
    };
  }[];
  product?: {
    id: string;
    name: string;
    images: string[];
  };
  price?: {
    id: string;
    unit_amount: number;
    currency: string;
    type: string;
    recurring: boolean | null;
    billing_scheme: string;
  };
  latest_charge?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    refunded?: boolean;
  };
  charge?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    refunded?: boolean;
  };
  payment_intent?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
  };
  refund?: {
    id: string;
    amount: number;
    currency: string;
    status: string;
  };
  promotion_code?: {
    id: string;
    code?: string | null;
    coupon?: {
      id: string;
      amount_off: number | null;
      currency: string | null;
      name: string | null;
      percent_off: number | null;
      redeem_by: number | null;
    };
  };
  customers?: {
    customer: {
      id: string;
      name: string | null;
      email: string | null;
      total_spend_amount: number;
      total_spend_currency: string;
      last_payment_date: string | null;
    };
  }[];
  customer?: {
    id: string;
    name: string | null;
    email: string | null;
    total_spend_amount: number;
    total_spend_currency: string;
    last_payment_date: string | null;
  };
  plan?: AgentPlanItem[];
  plan_short_title?: string;
};

export type AgentCall = {
  tool_id: string;
  tool_name: string;
  tool_args: string;
  timestamp: number;
  has_response: boolean;
  response: AgentCallResponse;
};

export type ToolPlanItem = AgentPlanItem;
export type ToolCallResponse = AgentCallResponse & {
  plan?: ToolPlanItem[];
};
export type ToolCall = AgentCall & {
  response: ToolCallResponse;
};

export type ButtonsConversationMessage = BaseConversationMessage &
  ButtonsMessage;

export type InlineDatum = Record<string, unknown> & {
  name: string;
  datum_type: string;
};

export type ConversationInlineData = Record<string, unknown> & {
  name: string;
  style?: 'bubble' | 'outer_bubble' | 'none';
  isDone?: boolean;
  // FIXME: The data should always be in shape Array<InlineDatum>
  //  we should update the shim to return it in a consistent shape.
  //  We should also consider returning the stripe-assistant responses
  //  as a first-class citizen instead of embedding them in inline data.
  data:
    | Array<InlineDatum>
    | StripeAssistantCommandResponse
    | StripeAssistantLiveAnswersResponse;
};

export type InlineDataMessage = {
  type: 'inline-data-conversation-message';
  // TODO: Remove this once UH conversation message IDs are set to the effect IDs.
  effectId: string;
  inlineData: ConversationInlineData;
  responseId?: string;
  shouldHideAfterThumbsDown?: boolean;
};

export type InlineDataConversationMessage = BaseConversationMessage &
  InlineDataMessage;

export type InlineDataLogItem = ConversationLogItemMessageAdded & {
  message: InlineDataConversationMessage;
};

export type InlineDataAction = {
  name: 'inline-data-action';
  data?: Record<string, unknown>;
  nonce?: string;
};

export type InlineDataActionResult = {
  name: 'inline-data-action-result';
  data?: Record<string, unknown>;
  nonce?: string;
};

export interface StripeAssistantAction {
  humanText: string;
  searchFilter?: {
    filters: Record<string, unknown>;
  };
  userQuery?: string;
}

export interface StripeAssistantCommandResponseResult {
  text?: string;
  actions?: Array<StripeAssistantAction>;
  suggestedActions?: Array<StripeAssistantAction>;
}

export interface StripeAssistantCommandResponse {
  type: 'analyze_data';
  classification?: string;
  result: StripeAssistantCommandResponseResult;
  result_status: string;
  function_name: string;
}

export type StripeAssistantLiveAnswersSource = {
  title?: string;
  subtitle?: string;
  collection?: string;
  url: string;
  score: number;
};

export interface StripeAssistantLiveAnswersResponse {
  type: 'live_answers_v2' | 'fully_async';
  llm_conversation_id: string;
  cache_uuid: string;
  sa_response_id: string;
  sources: Array<StripeAssistantLiveAnswersSource>;
  is_streamed?: boolean;
  content?: string;
  // we translate it to "tool_calls", but it might come in as "agent_calls"
  agent_calls?: Array<ToolCall>;
  tool_calls?: Array<ToolCall>;
}

export type RichTextConversationMessage = BaseConversationMessage & {
  type: 'rich-text-conversation-message';
  text: string;
  data:
    | ButtonOptionsMessage
    | SelectOptionsMessage
    | ArticlesMessage
    | InlineDataMessage
    | ButtonsMessage;
};

export type ConversationMessage =
  | TextConversationMessage
  | AttachmentConversationMessage
  | ButtonOptionsConversationMessage
  | SelectOptionsConversationMessage
  | ArticlesConversationMessage
  | ButtonsConversationMessage
  | InlineDataConversationMessage
  | RichTextConversationMessage;

export type ConversationStarted = {
  name: 'conversation-started';
  conversationId: string;
};

export type ConversationEnded = {
  name: 'conversation-ended';
  conversationId: string;
};

export type ConversationUpdated = {
  name: 'conversation-updated';
  conversationId: string;
  status: ConversationStatus;
};

export type ConversationParticipantJoined = {
  name: 'conversation-participant-joined';
  conversationId: string;
  participant: ConversationParticipant;
};

export type ConversationParticipantLeft = {
  name: 'conversation-participant-left';
  conversationId: string;
  participant: ConversationParticipant;
};

export type ConversationParticipantUpdated = {
  name: 'conversation-participant-updated';
  conversationId: string;
  participant: ConversationParticipant;
};

export type ConversationMessageAdded = {
  name: 'conversation-message-added';
  conversationId: string;
  participantId: string;
  messageId: string;
  nonce?: string;
  message?: ConversationMessage;
};

export type ConversationMessageUpdated = {
  name: 'conversation-message-updated';
  conversationId: string;
  participantId?: string;
  messageId: string;
  nonce?: string;
  message?: ConversationMessage;
};

export type ConversationTypingStarted = {
  name: 'conversation-typing-started';
  conversationId: string;
  participantId: string;
};

export type ConversationTypingEnded = {
  name: 'conversation-typing-ended';
  conversationId: string;
  participantId: string;
};

export type ConversationSetContextValues = {
  name: 'conversation-set-context-values';
  conversationId: string;
  values: ConversationContext;
};

export type ConversationClearContextValues = {
  name: 'conversation-clear-context-values';
  conversationId: string;
  keys: string[];
};

export type ConversationError = {
  name: 'conversation-error';
  conversationId: string;
  error: string;
  sentryEventId?: string;
};

export type ConversationException = {
  name: 'conversation-exception';
  conversationId: string;
  error: string;
  sentryEventId?: string;
};

export type ConversationRequestAction = {
  name: 'conversation-request-action';
  conversationId: string;
  action: Action;
  respondWith?: (data: Record<string, unknown> | undefined) => void;
  responseTimeout?: number;
};

export type ConversationOptionSelected = {
  name: 'conversation-option-selected';
  conversationId: string;
  optionId: string;
};

// FIXME: Remove this; this should not be the concern of this package.
export type ConversationTriggerScrollToTop = {
  name: 'conversation-trigger-scroll-to-top';
  conversationId: string;
};

export type ConversationEventType =
  | ConversationStarted
  | ConversationEnded
  | ConversationUpdated
  | ConversationParticipantJoined
  | ConversationParticipantLeft
  | ConversationParticipantUpdated
  | ConversationMessageAdded
  | ConversationMessageUpdated
  | ConversationTypingStarted
  | ConversationTypingEnded
  | ConversationSetContextValues
  | ConversationClearContextValues
  | ConversationError
  | ConversationException
  | ConversationRequestAction
  | ConversationOptionSelected
  | ConversationTriggerScrollToTop;

export type ConversationEvent = ConversationEventType & BaseEvent;

export type ClientResponse = {
  code: number;
  data?: unknown;
  error?: string;
};

export type ClientErrorResponse = ClientResponse & {
  error: string;
  sentryEventId?: string;
};

export type ConversationSyncStorageMode =
  | 'hydrate'
  | 'sync'
  | undefined
  | false;

type ExtraParams = Record<string, unknown>;

export interface StartConversationParams extends ExtraParams {
  token?: string;
  conversationId?: string;
  context?: ConversationContext;
  participant?: ConversationParticipant;
  syncStorage?: ConversationSyncStorageMode;
  fetch?: boolean;
  connect?: boolean;
  requestAnalyticsEventId?: () => Promise<string | null>;
  uhConversationId?: string;
  data?: Record<string, unknown>;
}

export interface ConversationResponseData {
  conversation: Conversation;
}

export interface LeaveConversationParams {
  token?: string;
  conversationId: string;
  participant: ConversationParticipant;
}

export interface GetConversationMessagesParams {
  token?: string;
  conversationId: string;
  messageIds?: string[];
}

export interface ConversationMessagesResponseData {
  messages: Record<string, ConversationMessage>;
}

export interface GetConversationParticipantsParams {
  token?: string;
  conversationId: string;
  participantIds?: string[];
}

export interface ConversationParticipantsResponseData {
  participants: Record<string, ConversationParticipant>;
}

export interface SendConversationMessageParams {
  token?: string;
  conversationId: string;
  message: ConversationMessage;
}

export interface SendConversationTypingParams {
  token?: string;
  conversationId: string;
  participantId: string;
}

export interface SendFeedbackParams {
  conversationId: string;
  responseId: string;
  feedback: 'thumbs_up' | 'thumbs_down' | 'none';
}

export interface StreamTextParams {
  conversationId: string;
  saResponseId: string;
  effectId: string;
  streamId: string;
  offset: number;
  cacheUuid: string;
  signal: AbortSignal;
}

export type StreamTextResponseData =
  | {
      type: 'success';
      llm_conversation_id: string;
      content: string;
      is_complete: boolean;
      was_halted: boolean;
      next_offset: number;
      agent_calls?: AgentCall[];
      sources?: Array<StripeAssistantLiveAnswersSource>;
      status:
        | 'complete'
        | 'escalate_to_human_support'
        | 'halted'
        | 'preparing_response'
        | 'streaming_response'
        | 'trigger_resolution_flow'
        | 'unanswerable'
        | 'adversarial'
        | 'unspecified';
      not_answerable: boolean;
      progress_update?: string;
      is_long_running?: boolean;
    }
  | {
      type: 'error';
      error: string;
    };

export type StreamTextResponse = ClientResponse & {
  data?: StreamTextResponseData;
};

export interface Action {
  name: string;
  data?: Record<string, unknown>;
}

export interface RequestAction extends Action {
  name: 'request-action';
  action: Action;
  waitForResponse?: boolean;
  responseTimeout?: number;
}

export interface SelectOptionAction extends Action {
  name: 'select-option';
  message: ButtonOptionsMessage | SelectOptionsMessage;
  selectedOptionId: string;
}

export interface InlineDataResultExtra extends Record<string, unknown> {
  outcome?: string;
  dataWithPii?: Record<string, unknown>;
}

export type InlineDataResult = {
  type: 'success' | 'failure' | 'error';
  message?: string;
  // TODO: data should change to the inline data value
  //  as this is useful to have in the result as it can change
  //  over time since it was first rendered. it's also
  //  more consistent with the InlineData.data property.
  data?: InlineDataResultExtra;

  extra?: InlineDataResultExtra;
};

export interface InlineDataResultAction extends Action {
  name: 'inline-data-result';
  message: InlineDataMessage;
  result: InlineDataResult;
}

export interface GetSmartSuggestionsAction extends Action {
  name: 'get-smart-suggestions';
}

export interface HandleTriggerParamsAction extends Action {
  name: 'handle-trigger-params';
  triggerParams: TriggerParams;
}

export interface ThumbsDownAdvanceAction extends Action {
  name: 'thumbs-down-advance';
}

export type ConversationAction =
  | (Action &
      (
        | RequestAction
        | SelectOptionAction
        | InlineDataAction
        | InlineDataResultAction
        | GetSmartSuggestionsAction
        | HandleTriggerParamsAction
        | ThumbsDownAdvanceAction
      ))
  | Record<string, unknown>;

export interface ConversationActionParams {
  token?: string;
  conversationId: string;
  action: ConversationAction;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface RequestACallParams extends ExtraParams {
  conversationId: string;
  name: string;
  phoneNumber: string;
  message: string;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface GetVerificationCodesParams {
  conversationId: string;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface GetVerificationCodesResponseData {
  code: number;
  challengeCode: number;
  duration: number;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface SubmitEmailIssueParams extends ExtraParams {
  conversationId: string;
  email: string;
  body: string;
}

export interface InProgressParams {
  conversationId?: string;
  context?: ConversationContext;
}

export type ConversationChannel = 'bot' | 'chat' | 'request_a_call' | 'email';

export interface TriggerParams {
  triggerPoint: string;
  initialGreeting?: string;
  initialQuery?: string;
  refcode?: string;
}

export interface InProgressData {
  conversationId?: string;
  channel?: ConversationChannel;
  clientType?: string;
  inProgress: boolean;
  extra?: Record<string, unknown>;
}

export type StartConversationResponse = ClientResponse &
  (ClientErrorResponse | {data: ConversationResponseData});
export type GetConversationMessagesResponse = ClientResponse &
  (ClientErrorResponse | {data: ConversationMessagesResponseData});
export type GetConversationParticipantsResponse = ClientResponse &
  (ClientErrorResponse | {data: ConversationParticipantsResponseData});

export type GetVerificationCodesResponse = ClientResponse &
  (ClientErrorResponse | {data: GetVerificationCodesResponseData});

export type InProgressResponse = ClientResponse &
  (ClientErrorResponse | {data: InProgressData});

// FIXME: Remove this; this should not be the concern of this package.
export interface RequestACallClientUrls {
  requestACallUrl: string;
  verificationCodesUrl: string;
  channelsUrl: string;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface EmailIssueClientUrls {
  emailIssueUrl: string;
}

// FIXME: Remove the RAC/Email URLs
export interface ClientUrls
  extends RequestACallClientUrls,
    EmailIssueClientUrls {
  tokenUrl: string;
  conversationUrl: string;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface RequestACallClient {
  requestACall: (params: RequestACallParams) => Promise<ClientResponse>;
  getVerificationCodes: (
    params: GetVerificationCodesParams,
  ) => Promise<GetVerificationCodesResponse>;
  inProgress: (params: InProgressParams) => Promise<InProgressResponse>;
}

// FIXME: Remove this; this should not be the concern of this package.
export interface EmailIssueClient {
  submitEmailIssue: (params: SubmitEmailIssueParams) => Promise<ClientResponse>;
}

// FIXME: Remove extending the RAC/Email clients
export interface Client extends RequestACallClient, EmailIssueClient {
  channel: ConversationChannel;
  type: string;

  syncStorageMode?: ConversationSyncStorageMode;

  getStatus: () => ClientStatus;

  disconnect: () => Promise<void>;

  inProgress: (params: InProgressParams) => Promise<InProgressResponse>;

  startConversation: (
    params: StartConversationParams,
  ) => Promise<StartConversationResponse>;
  leaveConversation: (
    params: LeaveConversationParams,
  ) => Promise<ClientResponse>;

  getConversationMessages: (
    params: GetConversationMessagesParams,
  ) => Promise<GetConversationMessagesResponse>;
  getConversationParticipants: (
    params: GetConversationParticipantsParams,
  ) => Promise<GetConversationParticipantsResponse>;

  sendConversationMessage: (
    params: SendConversationMessageParams,
  ) => Promise<ClientResponse>;
  sendConversationTyping: (
    params: SendConversationTypingParams,
  ) => Promise<ClientResponse>;
  sendFeedback: (params: SendFeedbackParams) => Promise<ClientResponse>;

  streamText: (params: StreamTextParams) => Promise<StreamTextResponse>;

  action: (params: ConversationActionParams) => Promise<ClientResponse>;

  statusObservable: () => Observable<ClientStatus>;
  eventsObservable: () => Observable<ClientEvent>;
  conversationEventsObservable: () => Observable<ConversationEvent>;

  addListener: (listener: (event: ClientEvent) => void) => void;
  removeListener: (listener: (event: ClientEvent) => void) => void;

  addConversationListener: (
    id: string,
    listener: (event: ConversationEvent) => void,
  ) => void;
  removeConversationListener: (
    id: string,
    listener: (event: ConversationEvent) => void,
  ) => void;

  analytics: Analytics;
  errors: Errors;
  metrics: Metrics;

  // FIXME: This should not be a part of this package and the clients should not
  //  be holding state either
  hasShownInitialGreeting?: () => boolean;
  // FIXME: This should not be a part of this package and the clients should not
  //  be holding state either
  markInitialGreetingAsShown?: () => void;
}

// FIXME: Remove this; this should not be the concern of this package.
export type RequestACallDetails = {
  userName: string;
  userPhoneNumber: string;
  callCanceled: boolean;
};
